import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Row, Col, Card, Typography, Button, Avatar } from "antd";
import {
  ArrowLeftOutlined,
  EyeOutlined,
  EnvironmentOutlined,
  UserOutlined,
  TrophyFilled,
} from "@ant-design/icons";
import { decryptMessage } from "../../utils/decrypt";
import { getToken } from "../../services/auth";
import {
  CONTEST_MANAGEMENT_BASE,
  API_BASE_URL,
} from "../../services/apiConstant";
import axios from "axios";

const { Title } = Typography;

interface ParticipantImage {
  id: string;
  participantID: string;
  fileName: string;
  fileUrl: string;
}

interface ContestParticipantViewResponse {
  id: string;
  contestID: string;
  fullName: string;
  emailAddress: string;
  address: string;
  city: string;
  countryName: string;
  profileUrl: string;
  status: number;
  isWinner: boolean;
  imagesCount: number;
  participantImagesResponse: ParticipantImage[];
}

interface ContestResponse {
  id: string;
  title: string;
  description: string;
  contestParticipantViewResponse: ContestParticipantViewResponse[];
}

const ParticipantsList: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [contest, setContest] = useState<ContestResponse | null>(null);

  const { contestId } = location.state || {};

  useEffect(() => {
    const fetchContest = async () => {
      const token = getToken();
      if (!token) {
        console.error("No token found");
        return;
      }

      try {
        if (!contestId) return;
        const url = `${API_BASE_URL}${CONTEST_MANAGEMENT_BASE}/${contestId}`;
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const decryptedData = decryptMessage(response.data.Data, token);

        setContest(decryptedData);
      } catch (error) {
        console.error("Error fetching contest:", error);
      }
    };

    fetchContest();
  }, [contestId]);

  return (
    <div style={{ padding: "20px" }}>
      {/* Header Section */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16, color: '#41787C' }}>
        <Col>
          <Typography.Title level={3} style={{ margin: 0, color: '#41787C' }}>
            <Button
              type="link"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
              style={{ marginBottom: "10px", padding: 0 }}
            />
            Active Contest
          </Typography.Title>
        </Col>
        <Col>
          <Button
            type="primary"
            className="submit-btn btn-publish"
            icon={<TrophyFilled style={{ fontSize: 16 }} />}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/contest-support/winner-list`, { state: { contestId: contestId } });
            }}
          >
            Winner Management
          </Button>
        </Col>
      </Row>

      {contest ? (
        <>
          {/* Contest Info */}
          {/* <div style={{ marginBottom: 20 }}>
            <Title level={4}>{contest.title}</Title>
            <div
              dangerouslySetInnerHTML={{ __html: contest.description }}
              style={{ color: "#555" }}
            />
          </div> */}

          {/* Participants */}
          <Row gutter={[16, 16]}>
            {contest.contestParticipantViewResponse?.length > 0 ? (
              contest.contestParticipantViewResponse.map((pvr) => {
                const firstImage =
                  pvr.participantImagesResponse?.length > 0
                    ? pvr.participantImagesResponse[0].fileUrl
                    : null;

                return (
                  <Col key={pvr.id} xs={24} sm={12} md={8}>
                    <Card
                      hoverable
                      style={{
                        borderRadius: "12px",
                        overflow: "hidden",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      }}
                      cover={
                        firstImage ? (
                          <img
                            alt="Participant"
                            src={firstImage}
                            style={{ height: 150, objectFit: "cover" }}
                          />
                        ) : (
                          <div
                            style={{
                              height: 150,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: "#f0f0f0",
                              color: "#999",
                            }}
                          >
                            No Image
                          </div>
                        )
                      }
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          // borderBottom: "1px solid #f0f0f0", // optional separator between rows
                        }}
                      >
                        {/* Left section: Avatar + Name + Email + Location */}
                        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                          <Avatar
                            size={40} // slightly bigger for better visibility
                            src={pvr.profileUrl}
                            icon={!pvr.profileUrl && <UserOutlined />}
                          />
                          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <Title level={5} style={{ margin: 0, lineHeight: 1.2 }}>
                              {pvr.fullName}
                            </Title>
                            <span style={{ fontSize: 13, color: "#555" }}>{pvr.emailAddress}</span>
                            {(pvr.city || pvr.countryName) && (
                              <span
                                style={{
                                  fontSize: 12,
                                  color: "#e67e22",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                }}
                              >
                                <EnvironmentOutlined style={{ fontSize: 14, color: "#e67e22" }} />
                                {pvr.city}, {pvr.countryName}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Right section: Action button */}
                        <Button
                          type="text"
                          icon={<EyeOutlined style={{ fontSize: 20, color: "#555" }} />}
                          onClick={() =>
                            navigate("/participantDetails", {
                              state: {
                                participantId: pvr.id,
                                contestId: contest.id,
                                fullname: pvr.fullName,
                              },
                            })
                          }
                          style={{ padding: 0 }}
                        />
                      </div>


                    </Card>
                  </Col>
                );
              })
            ) : (
              <p>No participants found.</p>
            )}
          </Row>
        </>
      ) : (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "70vh" }}>
          <img
            src="/images/newLoading.gif" // <-- path to your GIF
            alt="Loading..."
            style={{ width: 200, height: 150 }}
          />
        </div>
      )}
    </div>
  );
};

export default ParticipantsList;

